import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Button } from '../Button';
import { Icon } from '../Icon';
import './ExportFlow.css';

export interface ExportConfig {
  // Период
  periodType: 'custom' | 'month' | 'quarter' | 'year' | 'all';
  startDate?: string;
  endDate?: string;
  selectedMonth?: number; // 1-12
  selectedYear?: number;
  selectedQuarter?: number; // 1-4
  
  // Типы данных
  includeExpenses: boolean;
  includeIncomes: boolean;
  includeGoals: boolean;
  includeRecurring: boolean; // 🆕 Регулярные платежи
  includeStatistics: boolean; // 🆕 Статистика и аналитика
  
  // Поля
  fields: {
    title: boolean;
    amount: boolean;
    category: boolean;
    date: boolean;
    time: boolean; // 🆕 Время отдельно от даты
    paymentMethod: boolean;
    isRecurring: boolean;
    description: boolean;
    balance: boolean;
    transactionId: boolean; // 🆕 ID транзакции
    tags: boolean; // 🆕 Теги/метки
    createdBy: boolean; // 🆕 Кто создал
    modifiedAt: boolean; // 🆕 Дата изменения
  };
}

interface ExportFlowProps {
  onClose: () => void;
  onExport: (config: ExportConfig) => Promise<void>;
  currency: string;
}

export const ExportFlow: React.FC<ExportFlowProps> = ({
  onClose,
  onExport,
  currency
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'period' | 'data' | 'fields'>('period');
  const [isExporting, setIsExporting] = useState(false);
  
  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Восстанавливаем скролл при закрытии
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);
  
  // Конфигурация экспорта
  const [config, setConfig] = useState<ExportConfig>({
    periodType: 'month',
    selectedMonth: new Date().getMonth() + 1,
    selectedYear: new Date().getFullYear(),
    includeExpenses: true,
    includeIncomes: true,
    includeGoals: false,
    includeRecurring: false, // 🆕 Регулярные платежи
    includeStatistics: false, // 🆕 Статистика и аналитика
    fields: {
      title: true,
      amount: true,
      category: true,
      date: true,
      time: false, // 🆕 Время отдельно от даты
      paymentMethod: true,
      isRecurring: true,
      description: false,
      balance: false,
      transactionId: false, // 🆕 ID транзакции
      tags: false, // 🆕 Теги/метки
      createdBy: false, // 🆕 Кто создал
      modifiedAt: false, // 🆕 Дата изменения
    }
  });

  // Быстрые пресеты периодов - сразу переход к следующему шагу
  const applyPreset = (preset: string) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    switch (preset) {
      case 'current-month':
        setConfig({
          ...config,
          periodType: 'month',
          selectedMonth: currentMonth + 1,
          selectedYear: currentYear
        });
        break;
      case 'last-month':
        const lastMonth = currentMonth === 0 ? 12 : currentMonth;
        const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        setConfig({
          ...config,
          periodType: 'month',
          selectedMonth: lastMonth,
          selectedYear: lastMonthYear
        });
        break;
      case 'last-3-months':
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        setConfig({
          ...config,
          periodType: 'custom',
          startDate: threeMonthsAgo.toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        });
        break;
      case 'last-6-months':
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        setConfig({
          ...config,
          periodType: 'custom',
          startDate: sixMonthsAgo.toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        });
        break;
      case 'current-year':
        setConfig({
          ...config,
          periodType: 'year',
          selectedYear: currentYear
        });
        break;
      case 'last-year':
        setConfig({
          ...config,
          periodType: 'year',
          selectedYear: currentYear - 1
        });
        break;
    }
    
    // Сразу переходим к следующему шагу - выбор данных
    setStep('data');
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
  const months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];
  const quarters = ['Q1 (Янв-Мар)', 'Q2 (Апр-Июн)', 'Q3 (Июл-Сен)', 'Q4 (Окт-Дек)'];

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await onExport(config);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      alert(t('export.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleBack = () => {
    if (step === 'data') {
      setStep('period');
    } else if (step === 'fields') {
      setStep('data');
    } else {
      onClose();
    }
  };

  const handleNext = () => {
    if (step === 'period') {
      setStep('data');
    } else if (step === 'data') {
      setStep('fields');
    }
  };

  const canProceed = () => {
    if (step === 'period') {
      if (config.periodType === 'custom') {
        return config.startDate && config.endDate;
      }
      return true;
    }
    if (step === 'data') {
      return config.includeExpenses || config.includeIncomes || config.includeGoals || 
             config.includeRecurring || config.includeStatistics;
    }
    if (step === 'fields') {
      return Object.values(config.fields).some(v => v);
    }
    return true;
  };

  return createPortal(
    <div className="export-flow">
      <div className="export-flow__overlay" onClick={onClose} />
      <div className="export-flow__content">
        {/* Header */}
        <div className="export-flow__header">
          <button className="export-flow__back neo-raised" onClick={handleBack}>
            <Icon name="arrow-back" size={24} />
          </button>
          <h2 className="export-flow__title">
            Экспорт транзакций
          </h2>
          <button className="export-flow__close neo-raised" onClick={onClose}>
            <Icon name="close" size={24} />
          </button>
        </div>

        {/* Step: Period Selection */}
        {step === 'period' && (
          <div className="export-flow__step">
            <h3 className="export-step__title">Выберите период</h3>
            
            {/* Быстрые пресеты */}
            <div className="export-quick-presets">
              <div className="export-quick-presets__title">
                Быстрый выбор периода:
              </div>
              <div className="export-quick-presets__buttons">
                <button 
                  className="export-preset-btn"
                  onClick={() => applyPreset('current-month')}
                >
                  Текущий месяц
                </button>
                <button 
                  className="export-preset-btn"
                  onClick={() => applyPreset('last-month')}
                >
                  Прошлый месяц
                </button>
                <button 
                  className="export-preset-btn"
                  onClick={() => applyPreset('last-3-months')}
                >
                  Последние 3 месяца
                </button>
                <button 
                  className="export-preset-btn"
                  onClick={() => applyPreset('last-6-months')}
                >
                  Последние 6 месяцев
                </button>
                <button 
                  className="export-preset-btn"
                  onClick={() => applyPreset('current-year')}
                >
                  Текущий год
                </button>
                <button 
                  className="export-preset-btn"
                  onClick={() => applyPreset('last-year')}
                >
                  Прошлый год
                </button>
              </div>
            </div>

            <div className="export-divider">
              <span>или выберите вручную</span>
            </div>
            
            <div className="export-period-types">
              <button 
                className={`export-period-type ${config.periodType === 'month' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, periodType: 'month' })}
              >
                <span>За месяц</span>
              </button>
              
              <button 
                className={`export-period-type ${config.periodType === 'quarter' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, periodType: 'quarter' })}
              >
                <span>За квартал</span>
              </button>
              
              <button 
                className={`export-period-type ${config.periodType === 'year' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, periodType: 'year' })}
              >
                <span>За год</span>
              </button>
              
              <button 
                className={`export-period-type ${config.periodType === 'custom' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, periodType: 'custom' })}
              >
                <span>Свой диапазон</span>
              </button>
              
              <button 
                className={`export-period-type ${config.periodType === 'all' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, periodType: 'all' })}
              >
                <span>За всё время</span>
              </button>
            </div>

            {/* Month Selection */}
            {config.periodType === 'month' && (
              <div className="export-period-details">
                <div className="export-field">
                  <label>Месяц</label>
                  <select
                    value={config.selectedMonth || new Date().getMonth() + 1}
                    onChange={(e) => setConfig({ ...config, selectedMonth: parseInt(e.target.value) })}
                  >
                    {months.map((month, index) => (
                      <option key={index} value={index + 1}>{month}</option>
                    ))}
                  </select>
                </div>
                
                <div className="export-field">
                  <label>Год</label>
                  <select
                    value={config.selectedYear || currentYear}
                    onChange={(e) => setConfig({ ...config, selectedYear: parseInt(e.target.value) })}
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Quarter Selection */}
            {config.periodType === 'quarter' && (
              <div className="export-period-details">
                <div className="export-field">
                  <label>Квартал</label>
                  <select
                    value={config.selectedQuarter || Math.floor(new Date().getMonth() / 3) + 1}
                    onChange={(e) => setConfig({ ...config, selectedQuarter: parseInt(e.target.value) })}
                  >
                    {quarters.map((quarter, index) => (
                      <option key={index} value={index + 1}>{quarter}</option>
                    ))}
                  </select>
                </div>
                
                <div className="export-field">
                  <label>Год</label>
                  <select
                    value={config.selectedYear || currentYear}
                    onChange={(e) => setConfig({ ...config, selectedYear: parseInt(e.target.value) })}
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Year Selection */}
            {config.periodType === 'year' && (
              <div className="export-period-details">
                <div className="export-field">
                  <label>Год</label>
                  <select
                    value={config.selectedYear || currentYear}
                    onChange={(e) => setConfig({ ...config, selectedYear: parseInt(e.target.value) })}
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Custom Date Range */}
            {config.periodType === 'custom' && (
              <div className="export-period-details">
                <div className="export-field">
                  <label>От</label>
                  <input
                    type="date"
                    value={config.startDate || ''}
                    onChange={(e) => setConfig({ ...config, startDate: e.target.value })}
                  />
                </div>
                
                <div className="export-field">
                  <label>До</label>
                  <input
                    type="date"
                    value={config.endDate || ''}
                    onChange={(e) => setConfig({ ...config, endDate: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step: Data Type Selection */}
        {step === 'data' && (
          <div className="export-flow__step">
            <h3 className="export-step__title">Что хотите выгрузить?</h3>
            
            <div className="export-checkboxes">
              <button 
                className={`export-checkbox ${config.includeExpenses ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, includeExpenses: !config.includeExpenses })}
              >
                <span className="export-checkbox__icon">
                  <Icon name="arrow-down" size={20} color="#FF3B30" />
                </span>
                <div className="export-checkbox__content">
                  <div className="export-checkbox__label">Расходы</div>
                  <div className="export-checkbox__desc">Все расходы за выбранный период</div>
                </div>
                {config.includeExpenses && (
                  <div className="export-checkbox__check">
                    <Icon name="checkmark" size={20} color="var(--color-text-primary)" />
                  </div>
                )}
              </button>

              <button 
                className={`export-checkbox ${config.includeIncomes ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, includeIncomes: !config.includeIncomes })}
              >
                <span className="export-checkbox__icon">
                  <Icon name="arrow-up" size={20} color="#34C759" />
                </span>
                <div className="export-checkbox__content">
                  <div className="export-checkbox__label">Доходы</div>
                  <div className="export-checkbox__desc">Все доходы за выбранный период</div>
                </div>
                {config.includeIncomes && (
                  <div className="export-checkbox__check">
                    <Icon name="checkmark" size={20} color="var(--color-text-primary)" />
                  </div>
                )}
              </button>

              <button 
                className={`export-checkbox ${config.includeGoals ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, includeGoals: !config.includeGoals })}
              >
                <span className="export-checkbox__icon">
                  <Icon name="target" size={20} color="#007AFF" />
                </span>
                <div className="export-checkbox__content">
                  <div className="export-checkbox__label">Цели</div>
                  <div className="export-checkbox__desc">Информация о накоплениях и целях</div>
                </div>
                {config.includeGoals && (
                  <div className="export-checkbox__check">
                    <Icon name="checkmark" size={20} color="var(--color-text-primary)" />
                  </div>
                )}
              </button>

              <button 
                className={`export-checkbox ${config.includeRecurring ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, includeRecurring: !config.includeRecurring })}
              >
                <span className="export-checkbox__icon">
                  <Icon name="repeat" size={20} color="#FF9500" />
                </span>
                <div className="export-checkbox__content">
                  <div className="export-checkbox__label">Регулярные платежи</div>
                  <div className="export-checkbox__desc">Подписки и периодические операции</div>
                </div>
                {config.includeRecurring && (
                  <div className="export-checkbox__check">
                    <Icon name="checkmark" size={20} color="var(--color-text-primary)" />
                  </div>
                )}
              </button>

              <button 
                className={`export-checkbox ${config.includeStatistics ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, includeStatistics: !config.includeStatistics })}
              >
                <span className="export-checkbox__icon">
                  <Icon name="bar-chart" size={20} color="#5856D6" />
                </span>
                <div className="export-checkbox__content">
                  <div className="export-checkbox__label">Статистика</div>
                  <div className="export-checkbox__desc">Аналитика и сводная информация</div>
                </div>
                {config.includeStatistics && (
                  <div className="export-checkbox__check">
                    <Icon name="checkmark" size={20} color="var(--color-text-primary)" />
                  </div>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step: Fields Selection */}
        {step === 'fields' && (
          <div className="export-flow__step">
            <h3 className="export-step__title">Выберите поля для экспорта</h3>
            
            <div className="export-fields">
              <button 
                className={`export-field-checkbox ${config.fields.title ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, title: !config.fields.title }
                })}
              >
                <span>Название</span>
                {config.fields.title && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.amount ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, amount: !config.fields.amount }
                })}
              >
                <span>Сумма</span>
                {config.fields.amount && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.category ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, category: !config.fields.category }
                })}
              >
                <span>Категория</span>
                {config.fields.category && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.date ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, date: !config.fields.date }
                })}
              >
                <span>Дата</span>
                {config.fields.date && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.paymentMethod ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, paymentMethod: !config.fields.paymentMethod }
                })}
              >
                <span>Счёт / Метод оплаты</span>
                {config.fields.paymentMethod && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.isRecurring ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, isRecurring: !config.fields.isRecurring }
                })}
              >
                <span>Регулярность</span>
                {config.fields.isRecurring && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.description ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, description: !config.fields.description }
                })}
              >
                <span>Описание / Примечание</span>
                {config.fields.description && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.balance ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, balance: !config.fields.balance }
                })}
              >
                <span>Баланс после операции</span>
                {config.fields.balance && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.time ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, time: !config.fields.time }
                })}
              >
                <span className="export-field-label">
                  <Icon name="clock" size={16} color="#8E8E93" /> Время (отдельно от даты)
                </span>
                {config.fields.time && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.transactionId ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, transactionId: !config.fields.transactionId }
                })}
              >
                <span className="export-field-label">
                  <Icon name="key" size={16} color="#8E8E93" /> ID транзакции
                </span>
                {config.fields.transactionId && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.tags ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, tags: !config.fields.tags }
                })}
              >
                <span className="export-field-label">
                  <Icon name="tag" size={16} color="#8E8E93" /> Теги / Метки
                </span>
                {config.fields.tags && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.createdBy ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, createdBy: !config.fields.createdBy }
                })}
              >
                <span className="export-field-label">
                  <Icon name="user" size={16} color="#8E8E93" /> Кто создал
                </span>
                {config.fields.createdBy && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>

              <button 
                className={`export-field-checkbox ${config.fields.modifiedAt ? 'active' : ''}`}
                onClick={() => setConfig({ 
                  ...config, 
                  fields: { ...config.fields, modifiedAt: !config.fields.modifiedAt }
                })}
              >
                <span className="export-field-label">
                  <Icon name="edit" size={16} color="#8E8E93" /> Дата изменения
                </span>
                {config.fields.modifiedAt && <Icon name="checkmark" size={16} color="var(--color-text-primary)" />}
              </button>
            </div>

            <div className="export-hint">
              <Icon name="lightbulb" size={18} color="var(--color-text-secondary)" /> Выбранные поля будут включены в Excel файл в указанном порядке
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="export-flow__actions">
          {step !== 'fields' ? (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!canProceed()}
              fullWidth
              className="neo-raised"
            >
              Далее
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={!canProceed() || isExporting}
              fullWidth
              className="neo-raised"
            >
              {isExporting ? 'Экспорт...' : (
                <>
                  <Icon name="download" size={20} color="currentColor" />
                  Экспортировать
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
